import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TableRowActionLink } from '@/components/ui/TableRowActions';
import { ApprovalDelegateEmployeeSelect } from '@/modules/approvals/components/ApprovalDelegateEmployeeSelect';
import { resolveApprovalDelegateUserId } from '@/modules/approvals/components/approvalDelegateUtils';
import type { ApprovalInstance } from '@/types/approvals';
import type { EmployeeResponse } from '@/types/employee';
import type { UiWorkflowContext } from '@/types/ui';
import { notify } from '@/core/toast/notify';
interface Props {
  row: ApprovalInstance;
  workflow?: UiWorkflowContext;
  comments: string;
  acting: boolean;
  currentUserId?: number;
  onCommentsChange: (value: string) => void;
  onAct: (action: string, delegateToUserId?: number) => void;
  /** Compact vertical layout for DataTable action columns. */
  variant?: 'default' | 'table';
  /** When set, overrides requester detection from row.requestedBy (e.g. leave owner). */
  isRequesterOverride?: boolean;
}

export function ApprovalActionPanel({
  row,
  workflow,
  comments,
  acting,
  currentUserId,
  onCommentsChange,
  onAct,
  variant = 'default',
  isRequesterOverride,
}: Props) {
  const { t } = useTranslation('workflow');
  const [delegateEmployeeId, setDelegateEmployeeId] = useState('');
  const [delegateEmployee, setDelegateEmployee] = useState<EmployeeResponse | null>(null);
  const workflowBound = workflow?.instanceId != null && workflow.instanceId > 0;
  const rowCanAct =
    row.canAct === true &&
    (row.approvalStatus === 'PENDING' || row.approvalStatus === 'ESCALATED');
  const allowed = new Set(workflow?.allowedActions ?? []);
  const canApprove = rowCanAct && (!workflowBound || !!workflow.canApprove);
  const canReject = rowCanAct && (!workflowBound || !!workflow.canReject);
  const canReturn = rowCanAct && (!workflowBound || !!workflow.canReturn);
  const canDelegate =
    rowCanAct && (workflowBound ? !!workflow.canDelegate : allowed.has('DELEGATE'));
  const canResubmit = workflowBound ? workflow.canResubmit : allowed.has('RESUBMIT');
  const canCancel = workflowBound ? workflow.canCancel : allowed.has('CANCEL');
  const isOpen = row.approvalStatus === 'PENDING' || row.approvalStatus === 'ESCALATED';
  const isReturned = row.approvalStatus === 'RETURNED';
  const isRequester =
    isRequesterOverride ??
    (currentUserId != null && row.requestedBy === currentUserId);
  const showResubmit = isReturned && isRequester;
  const showCancelRequest = (isOpen || isReturned) && isRequester && (canCancel || isReturned);
  const showActions = canApprove || canReject || canReturn || canDelegate || canResubmit || canCancel
    || showResubmit || showCancelRequest || (isOpen && isRequester);

  if (!showActions) return null;

  const submitDelegate = () => {
    if (!delegateEmployeeId.trim()) {
      notify.error(t('form.delegateRequired'));
      return;
    }
    const userId = resolveApprovalDelegateUserId(delegateEmployee, currentUserId);
    if (!userId) return;
    onAct('DELEGATE', userId);
    setDelegateEmployeeId('');
    setDelegateEmployee(null);
  };
  const cancelControl = showCancelRequest ? (
    variant === 'table' ? (
      <TableRowActionLink
        className="table-row-action-link--muted"
        disabled={acting}
        onClick={() => onAct('CANCEL')}
      >
        {t('actions.cancelRequest')}
      </TableRowActionLink>
    ) : (
      <Button size="sm" variant="ghost" disabled={acting} onClick={() => onAct('CANCEL')}>
        {t('actions.cancelRequest')}
      </Button>
    )
  ) : null;

  const resubmitControl = showResubmit ? (
    variant === 'table' ? (
      <TableRowActionLink disabled={acting} onClick={() => onAct('RESUBMIT')}>
        {t('actions.resubmit')}
      </TableRowActionLink>
    ) : (
      <Button size="sm" variant="secondary" disabled={acting} onClick={() => onAct('RESUBMIT')}>
        {t('actions.resubmit')}
      </Button>
    )
  ) : null;

  const approverControls =
    canApprove && isOpen ? (
      <>
        <Input
          placeholder={t('form.commentsPlaceholder')}
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
          className={
            variant === 'table'
              ? 'w-full min-w-0 text-xs'
              : 'w-full min-w-[160px] sm:max-w-[200px]'
          }
        />
        {variant === 'table' ? (
          <div className="flex flex-wrap items-start justify-start gap-x-2 gap-y-0.5">
            {canApprove ? (
              <TableRowActionLink disabled={acting} onClick={() => onAct('APPROVE')}>
                {t('actions.approve')}
              </TableRowActionLink>
            ) : null}
            {canReject ? (
              <TableRowActionLink
                className="table-row-action-link--muted"
                disabled={acting}
                onClick={() => onAct('REJECT')}
              >
                {t('actions.reject')}
              </TableRowActionLink>
            ) : null}
            {canReturn ? (
              <TableRowActionLink disabled={acting} onClick={() => onAct('RETURN')}>
                {t('actions.return')}
              </TableRowActionLink>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-start gap-1">
            {canApprove ? (
              <Button size="sm" disabled={acting} onClick={() => onAct('APPROVE')}>
                {t('actions.approve')}
              </Button>
            ) : null}
            {canReject ? (
              <Button size="sm" variant="secondary" disabled={acting} onClick={() => onAct('REJECT')}>
                {t('actions.reject')}
              </Button>
            ) : null}
            {canReturn ? (
              <Button size="sm" variant="ghost" disabled={acting} onClick={() => onAct('RETURN')}>
                {t('actions.return')}
              </Button>
            ) : null}
          </div>
        )}
        {canDelegate ? (
          <div
            className={
              variant === 'table'
                ? 'flex w-full flex-col items-start gap-1'
                : 'flex flex-wrap items-end gap-1'
            }
          >
            <ApprovalDelegateEmployeeSelect
              value={delegateEmployeeId}
              onChange={setDelegateEmployeeId}
              onEmployeeChange={setDelegateEmployee}
              excludeUserId={currentUserId}
              placeholder={t('labels.selectDelegate')}
              compact={variant === 'table'}
              className={
                variant === 'table'
                  ? 'w-full min-w-0 text-xs'
                  : 'w-full min-w-[12rem] sm:max-w-[16rem]'
              }
            />
            {variant === 'table' ? (
              <TableRowActionLink
                disabled={acting || !delegateEmployeeId.trim()}
                onClick={submitDelegate}
              >
                {t('actions.delegate')}
              </TableRowActionLink>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={acting || !delegateEmployeeId.trim()}
                onClick={submitDelegate}
              >
                {t('actions.delegate')}
              </Button>
            )}
          </div>
        ) : null}
      </>
    ) : null;

  return (
    <div
      className={
        variant === 'table'
          ? 'flex w-full min-w-[11rem] flex-col items-start gap-1.5'
          : 'flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center'
      }
    >
      {variant === 'table' ? (
        <>
          {cancelControl}
          {resubmitControl}
        </>
      ) : null}
      {approverControls}
      {variant !== 'table' ? (
        <>
          {resubmitControl}
          {cancelControl}
        </>
      ) : null}
    </div>
  );
}
